import React, { useContext, useEffect, useRef, useState } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom'
import API from '../services/API'
import { UserContext } from '../App'

// Browser pages can only observe activity inside the browser window (mouse,
// keys, touch, scroll) — OS-level activity in other applications is not
// visible to a web page, so working in another app still counts as idle here.
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'wheel',
]

const formatCountdown = (totalSeconds) => {
  const seconds = Math.max(0, totalSeconds)
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

/**
 * Signs the user out after a period of inactivity. Disabled unless `enabled`
 * is passed — mounted app-wide but currently switched on only for hospital
 * portal users (shared ward terminals).
 *
 * Flow: `idleMinutes` with no activity → warning popup with a
 * `countdownSeconds` countdown → any cursor movement / key press / Continue
 * dismisses it; reaching zero signs out. Deadlines are timestamps rather than
 * decrementing counters so background-tab timer throttling can't stretch them.
 */
const UserSessionIdleTimer = ({
  enabled = false,
  idleMinutes = 10,
  countdownSeconds = 180,
}) => {
  const { setUser } = useContext(UserContext)
  const [countdownOpen, setCountdownOpen] = useState(false)
  const [remainingSeconds, setRemainingSeconds] = useState(countdownSeconds)
  const lastActivityRef = useRef(Date.now())
  const countdownStartedAtRef = useRef(null)
  const logoutDeadlineRef = useRef(null)
  const loggingOutRef = useRef(false)

  useEffect(() => {
    if (!enabled) return undefined

    const markActivity = () => {
      lastActivityRef.current = Date.now()
    }

    ACTIVITY_EVENTS.forEach((eventName) =>
      window.addEventListener(eventName, markActivity, { passive: true }),
    )

    const logout = async () => {
      if (loggingOutRef.current) return
      loggingOutRef.current = true
      try {
        await API.delete('/logout')
      } catch (e) {
        console.error(e)
      }
      setUser({})
    }

    const tick = () => {
      const now = Date.now()

      if (!countdownStartedAtRef.current) {
        if (now - lastActivityRef.current >= idleMinutes * 60 * 1000) {
          countdownStartedAtRef.current = now
          logoutDeadlineRef.current = now + countdownSeconds * 1000
          setRemainingSeconds(countdownSeconds)
          setCountdownOpen(true)
        }
        return
      }

      // Countdown running — any activity since it started cancels it
      if (lastActivityRef.current > countdownStartedAtRef.current) {
        countdownStartedAtRef.current = null
        logoutDeadlineRef.current = null
        setCountdownOpen(false)
        return
      }

      const remaining = Math.ceil((logoutDeadlineRef.current - now) / 1000)
      setRemainingSeconds(remaining)
      if (remaining <= 0) {
        countdownStartedAtRef.current = null
        logoutDeadlineRef.current = null
        setCountdownOpen(false)
        logout()
      }
    }

    const interval = setInterval(tick, 1000)

    return () => {
      clearInterval(interval)
      ACTIVITY_EVENTS.forEach((eventName) =>
        window.removeEventListener(eventName, markActivity),
      )
    }
  }, [enabled, idleMinutes, countdownSeconds, setUser])

  if (!enabled || !countdownOpen) return null

  return (
    <Dialog open disableEscapeKeyDown maxWidth="xs" fullWidth>
      <DialogContent>
        <Stack spacing={2} alignItems="center" textAlign="center" py={1}>
          <HourglassBottomIcon color="warning" sx={{ fontSize: 44 }} />
          <Typography variant="h6" fontWeight={900}>
            Are you still there?
          </Typography>
          <Typography color="text.secondary">
            You've been inactive for a while. For patient confidentiality you
            will be signed out automatically in
          </Typography>
          <Typography variant="h3" fontWeight={900} color="warning.main">
            {formatCountdown(remainingSeconds)}
          </Typography>
          <LinearProgress
            variant="determinate"
            color="warning"
            value={(remainingSeconds / countdownSeconds) * 100}
            sx={{ width: '100%', borderRadius: 1 }}
          />
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={() => {
              lastActivityRef.current = Date.now()
              countdownStartedAtRef.current = null
              logoutDeadlineRef.current = null
              setCountdownOpen(false)
            }}
            sx={{ fontWeight: 800 }}
          >
            Continue session
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default UserSessionIdleTimer
